import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUserStore } from '../../store/userStore';
import { startRefreshTimer } from '../../api/api';
import { getSessionStore } from '../../store/sessionStore';
import { resolvePostLoginRoute, resolvePromptCount } from '../../utils/userProfile';

/**
 * GoogleOAuthCallback Component
 * Handles the OAuth callback from Google authentication
 * Backend redirects here with ?token=JWT or /error
 */
const GoogleOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const token = searchParams.get('token');

        if (!token) {
          throw new Error('No authentication token received');
        }

        // Store the JWT token
        localStorage.setItem('accessToken', token);

        // Optionally decode and extract user info from JWT
        // For now, let the app fetch user info with the token
        startRefreshTimer();

        const session = await getSessionStore().bootstrapSession({
          force: true,
        });
        const promptCount = resolvePromptCount(session.prompts ?? []);
        const userRecord = session.user as Record<string, unknown> | null;

        toast.success('Login successful!');
        setTimeout(() => navigate(resolvePostLoginRoute(userRecord, promptCount)), 500);
      } catch (err: any) {
        console.error('OAuth callback error:', err);
        toast.error(err.message || 'Failed to process authentication');
        setTimeout(() => navigate('/'), 2000);
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {loading && (
        <>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Completing authentication...</p>
        </>
      )}
    </div>
  );
};

export default GoogleOAuthCallback;
